import React, { useState, useRef, useEffect } from 'react';
import { useFirebaseAuth } from '../../contexts/FirebaseAuthContext';
import { useVehicles } from '../../hooks/queries';
import {
    UserCircleIcon,
    ArrowLeftOnRectangleIcon,
    ChevronDownIcon,
    ShieldCheckIcon
} from '../Icons';
import ConfirmationModal from '../shared/ConfirmationModal';
import { Timestamp } from 'firebase/firestore';

// We need to import constants correctly.
import { OWNER_UID as ENV_OWNER_UID } from '../../constants';

interface UserMenuProps {
    onNavigate: (view: any) => void; // Using 'any' for View type to avoid circular deps if View is not exported from types.ts cleanly or to keep it simple. Preferably import View from types.
}

// Better to invoke it with string literals matching View type for now.

const UserMenu: React.FC<UserMenuProps> = ({ onNavigate }) => {
    const { user, license, subscriptionActive, logout } = useFirebaseAuth();
    const { data: vehicles = [] } = useVehicles();
    const [isOpen, setIsOpen] = useState(false);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    const isOwner = user?.uid === ENV_OWNER_UID;

    // Click outside handler
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);

        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    if (!user) return null;

    const vehicleLimit = license?.vehicleLimit || 0;
    const vehicleCount = vehicles.length;

    // Format date
    const validToDate = license?.validTo ? (license.validTo as Timestamp).toDate() : null;
    const dateStr = validToDate ? validToDate.toLocaleDateString() : '—';

    // Status Logic
    const isLicenseExpired = license && validToDate && validToDate < new Date();
    const statusColor = isLicenseExpired ? 'text-red-600 bg-red-100' :
        license?.status === 'trial' ? 'text-orange-600 bg-orange-100' :
            'text-green-600 bg-green-100';
    const statusText = isLicenseExpired ? 'Истекла' :
        license?.status === 'trial' ? 'Пробный' : 'Активный';

    return (
        <div className="relative" ref={menuRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title="Меню пользователя"
            >
                <div className="flex flex-col items-end hidden sm:flex">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        {user.displayName || user.email?.split('@')[0]}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${statusColor}`}>
                        {statusText}
                    </span>
                </div>
                {user.photoURL ? (
                    <img src={user.photoURL} alt="User" className="w-10 h-10 rounded-full border border-gray-200" />
                ) : (
                    <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
                        <UserCircleIcon className="w-8 h-8" />
                    </div>
                )}
                <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-72 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 py-2 z-50">
                    <div className="px-4 py-3 border-b dark:border-gray-700">
                        <p className="text-sm text-gray-500 dark:text-gray-400">Лицензия</p>
                        <div className="flex justify-between items-center mt-1">
                            <span className="font-medium text-gray-900 dark:text-white">До {dateStr}</span>
                            <span className="text-xs text-gray-400">{license?.status?.toUpperCase()}</span>
                        </div>

                        <div className="mt-3">
                            <div className="flex justify-between text-xs mb-1">
                                <span className="text-gray-500">Транспорт</span>
                                <span className={vehicleCount >= vehicleLimit ? 'text-red-500 font-bold' : 'text-gray-700 dark:text-gray-300'}>
                                    {vehicleCount} / {vehicleLimit}
                                </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-1.5 dark:bg-gray-700">
                                <div
                                    className={`h-1.5 rounded-full ${vehicleCount >= vehicleLimit ? 'bg-red-500' : 'bg-blue-500'}`}
                                    style={{ width: `${Math.min((vehicleCount / Math.max(vehicleLimit, 1)) * 100, 100)}%` }}
                                ></div>
                            </div>
                        </div>
                    </div>

                    <div className="py-1">
                        {/* Owner Link */}
                        {isOwner && (
                            <button
                                onClick={() => { setIsOpen(false); onNavigate('OWNER_PANEL'); }}
                                className="w-full text-left px-4 py-2 text-sm text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 flex items-center gap-2"
                            >
                                <ShieldCheckIcon className="w-4 h-4" />
                                Панель Владельца
                            </button>
                        )}

                        <button
                            onClick={() => { setIsOpen(false); setShowLogoutConfirm(true); }}
                            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                        >
                            <ArrowLeftOnRectangleIcon className="w-4 h-4" />
                            Выйти
                        </button>
                    </div>
                </div>
            )}

            <ConfirmationModal
                isOpen={showLogoutConfirm}
                onClose={() => setShowLogoutConfirm(false)}
                onConfirm={async () => {
                    setShowLogoutConfirm(false);
                    await logout();
                }}
                title="Выход из системы"
                message="Вы уверены, что хотите выйти?"
                confirmText="Выйти"
            />
        </div>
    );
};

export default UserMenu;
